import { useState, type MouseEvent } from 'react';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { Copy, MoreVertical, Trash2 } from 'lucide-react';

import { Button } from '@/components/Button/Button';
import type { RawComponent } from '@/features/courses/useCourse';

import { CopyComponentDialog } from './CopyComponentDialog';
import { DeleteComponentDialog } from './DeleteComponentDialog';
import styles from './ComponentKebabMenu.module.css';

interface ComponentKebabMenuProps {
  component: RawComponent;
  componentId: string;
  componentTitle: string;
  componentUid?: number | string | null;
  currentCourseId: string;
  currentCourseTitle: string;
  isLive?: boolean;
  // A section divider is a component too, but only Delete applies to it: there
  // is no content to copy, and it can never be live.
  itemKind?: 'chapter' | 'divider';
  onSuccess(message: string): void;
}

export function ComponentKebabMenu({
  component,
  componentId,
  componentTitle,
  componentUid,
  currentCourseId,
  currentCourseTitle,
  isLive = false,
  itemKind = 'chapter',
  onSuccess,
}: ComponentKebabMenuProps) {
  const isDivider = itemKind === 'divider';
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isCopyDialogOpen, setIsCopyDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  return (
    <>
      <DropdownMenu.Root modal={false} onOpenChange={setIsMenuOpen} open={isMenuOpen}>
        <DropdownMenu.Trigger asChild>
          <Button
            aria-label={`Open ${isDivider ? 'section' : 'chapter'} actions for ${componentTitle}`}
            className={styles.trigger}
            iconOnly
            onClick={(event: MouseEvent<HTMLButtonElement>) => event.stopPropagation()}
            size="small"
            variant="ghost"
          >
            <MoreVertical aria-hidden="true" size={18} />
          </Button>
        </DropdownMenu.Trigger>

        <DropdownMenu.Portal>
          <DropdownMenu.Content
            align="end"
            className={styles.menuContent}
            sideOffset={8}
          >
            {isDivider ? null : (
              <DropdownMenu.Item
                className={styles.menuItem}
                onSelect={() => {
                  setIsMenuOpen(false);
                  setIsCopyDialogOpen(true);
                }}
              >
                <Copy aria-hidden="true" size={16} />
                <span>Copy</span>
              </DropdownMenu.Item>
            )}
            <DropdownMenu.Item
              className={[styles.menuItem, styles.dangerMenuItem].join(' ')}
              onSelect={() => {
                setIsMenuOpen(false);
                setIsDeleteDialogOpen(true);
              }}
            >
              <Trash2 aria-hidden="true" size={16} />
              <span>Delete</span>
            </DropdownMenu.Item>
          </DropdownMenu.Content>
        </DropdownMenu.Portal>
      </DropdownMenu.Root>

      {isCopyDialogOpen ? (
        <CopyComponentDialog
          component={component}
          componentTitle={componentTitle}
          currentCourseId={currentCourseId}
          currentCourseTitle={currentCourseTitle}
          onOpenChange={setIsCopyDialogOpen}
          onSuccess={onSuccess}
          open={isCopyDialogOpen}
        />
      ) : null}

      {isDeleteDialogOpen ? (
        <DeleteComponentDialog
          componentId={componentId}
          componentTitle={componentTitle}
          componentUid={componentUid}
          currentCourseId={currentCourseId}
          isLive={isLive}
          itemKind={itemKind}
          onOpenChange={setIsDeleteDialogOpen}
          onSuccess={onSuccess}
          open={isDeleteDialogOpen}
        />
      ) : null}
    </>
  );
}
